/**
 * Briques partagées par les modèles de home (réglette, palmarès…).
 * Garde une charte et un bas de page identiques d&apos;un modèle à l&apos;autre ;
 * seuls le hero et la section de tête diffèrent.
 */
import { Link } from "react-router-dom";

export function Check() {
  return (
    <svg className="ic" width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Feature({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="home-glass home-feature">
      <div className="fic">{icon}</div>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

export function Stat({ num, lab }: { num: string; lab: string }) {
  return (
    <div className="home-glass home-stat">
      <div className="num">{num}</div>
      <div className="lab">{lab}</div>
    </div>
  );
}

export function Step({ n, title, text }: { n: number; title: string; text: string }) {
  return (
    <li className="home-glass home-step">
      <span className="n" aria-hidden="true">{n}</span>
      <h3>{title}</h3>
      <p>{text}</p>
    </li>
  );
}

export function Faq({ q, a }: { q: string; a: string }) {
  return (
    <details className="home-glass">
      <summary>{q}</summary>
      <p>{a}</p>
    </details>
  );
}

/** Bande de réassurance, juste sous le hero — identique pour tous les modèles. */
export function TrustStrip() {
  return (
    <ul className="home-trust home-wrap">
      <li><Check /> Inspirée de la méthode 3CL-DPE 2021 du DPE réglementaire</li>
      <li><Check /> Données hébergées en France</li>
      <li><Check /> Estimation sans valeur légale</li>
    </ul>
  );
}

/** Bas de page commun : travaux & aides, bâti ancien, multi-biens, confiance,
 *  comment ça marche, FAQ, CTA final. */
export function CommonSections() {
  return (
    <>
      {/* TRAVAUX & AIDES */}
      <section className="home-wrap home-section">
        <div className="home-section-head">
          <span className="home-eyebrow">Simulation de travaux</span>
          <h2>Testez une rénovation, voyez vos aides.</h2>
          <p className="hint">
            Isolation, changement de chauffage… mesurez l&apos;effet sur votre étiquette
            et votre facture, et estimez ce qui reste à votre charge.
          </p>
        </div>
        <div className="home-split">
          <div className="home-glass pane">
            <div className="home-eyebrow">Avant / après</div>
            <div className="home-arrow">
              <span className="home-chip" style={{ background: "#ff6a2c" }}>F</span>
              <span aria-hidden="true">→</span>
              <span className="home-chip" style={{ background: "#cdd836" }}>C</span>
              <span>après isolation + pompe à chaleur</span>
            </div>
            <div className="home-gain">
              − 890 € / an
              <small>soit ≈ 1 870 € → 980 € sur la facture annuelle</small>
            </div>
          </div>
          <div className="home-glass pane">
            <div className="home-eyebrow">Aides estimées</div>
            <ul className="home-aids" style={{ marginTop: 14 }}>
              <li><Check /> <span><b>MaPrimeRénov&apos;</b> selon vos revenus</span></li>
              <li><Check /> <span><b>CEE</b> — primes énergie</span></li>
              <li><Check /> <span><b>Éco-PTZ</b> à taux zéro</span></li>
              <li><Check /> <span><b>Mensualité</b> estimée et capacité d&apos;emprunt</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* BÂTI ANCIEN */}
      <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
        <div className="home-glass home-ancien">
          <span className="em" aria-hidden="true">🏛️</span>
          <div>
            <h3>Une maison d&apos;avant 1948 ? On en tient compte.</h3>
            <p>
              Pierre, pisé, pan-de-bois : la méthode conventionnelle surestime souvent
              ces logements, et une isolation mal pensée peut les abîmer. Mon Diag Énergétique
              détecte le bâti ancien, vous alerte, et conseille des matériaux adaptés
              (chaux, isolants perspirants) plutôt qu&apos;une solution générique.
            </p>
          </div>
        </div>
      </section>

      {/* MULTI-BIENS */}
      <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
        <div className="home-glass home-multi">
          <div>
            <span className="home-eyebrow">Propriétaires & bailleurs</span>
            <h3>Un logement gratuit. Autant que nécessaire ensuite.</h3>
            <p>
              Gérez plusieurs biens, comparez-les, et suivez l&apos;évolution de
              leurs étiquettes énergétiques.
            </p>
          </div>
          <Link to="/register" className="home-btn home-btn-primary">
            Ajouter mes logements
          </Link>
        </div>
      </section>

      {/* CONFORMITÉ */}
      <section className="home-wrap home-section">
        <div className="home-section-head">
          <span className="home-eyebrow">Pourquoi nous faire confiance</span>
          <h2>Rigoureux sur la méthode, honnête sur les limites.</h2>
        </div>
        <div className="home-conformite">
          <Stat num="3CL-DPE 2021" lab="La méthode de calcul conventionnelle officielle, celle des diagnostiqueurs." />
          <Link to="/methodologie" style={{ textDecoration: "none" }}>
            <Stat num="> 99,9 %" lab="de classement conforme, vérifié sur 1,2 million de DPE de la base ADEME. → Détails" />
          </Link>
          <Stat num="Toujours une fourchette" lab="Jamais un chiffre unique trompeur, et jamais de recalage de votre étiquette." />
        </div>
      </section>

      {/* COMMENT ÇA MARCHE */}
      <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
        <div className="home-section-head">
          <span className="home-eyebrow">En trois étapes</span>
          <h2>Comment ça marche.</h2>
        </div>
        <ol className="home-steps">
          <Step n={1} title="Décrivez votre logement" text="Saisissez ses dimensions et ses caractéristiques : murs, fenêtres, chauffage." />
          <Step n={2} title="Obtenez votre étiquette" text="Le moteur 3CL-DPE 2021 calcule vos classes énergie et GES, et votre facture estimée." />
          <Step n={3} title="Testez des travaux" text="Simulez une rénovation et estimez vos aides MaPrimeRénov&apos; / CEE / éco-PTZ." />
        </ol>
      </section>

      {/* FAQ */}
      <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
        <div className="home-section-head">
          <span className="home-eyebrow">Questions fréquentes</span>
          <h2>Bon à savoir.</h2>
        </div>
        <div className="home-faq">
          <Faq q="Est-ce un DPE officiel ?" a="Non. Mon Diag Énergétique fournit une estimation fondée sur la méthode 3CL-DPE 2021, sans valeur légale. Un DPE officiel opposable nécessite un diagnostiqueur certifié qui se déplace." />
          <Faq q="C&apos;est vraiment gratuit ?" a="Oui pour votre premier logement, sans carte bancaire. Les biens suivants sont payants pour les propriétaires multi-biens." />
          <Faq q="Que deviennent mes données ?" a="Elles sont chiffrées, hébergées en France, et vous pouvez supprimer votre compte à tout moment." />
          <Faq q="Combien de temps ça prend ?" a="Environ 30 minutes pour une première estimation complète, que vous pouvez reprendre quand vous voulez — la saisie est sauvegardée en continu." />
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="home-wrap home-section" style={{ paddingTop: 0 }}>
        <div className="home-glass home-final">
          <h2>Découvrez la facture de votre logement.</h2>
          <p>Gratuit pour votre premier bien, sans rendez-vous, en 30 minutes.</p>
          <div className="home-cta-row">
            <Link to="/register" className="home-btn home-btn-primary">
              Commencer mon estimation gratuite
            </Link>
          </div>
          <p className="home-privacy">
            Estimation sans valeur légale · Données chiffrées et hébergées en France
          </p>
        </div>
      </section>
    </>
  );
}
